import {
	type Config,
	type PlanKey,
	config,
	getSellablePlanKeys,
	isPricingV2Live,
} from "@repo/config";
import {
	ADD_ONS,
	ADD_ON_KEYS,
	type AddOnBillingInterval,
	type AddOnKey,
	getAddOnPrice,
	getEntitlements,
	getPlanTier,
} from "@repo/payments/client";

/**
 * Pure data for the October 2026 ("v2") pricing UI: plan cards, the billing
 * toggle, Perplexity add-on tiers and the pricing-page JSON-LD. Reads the plan
 * config, the entitlements table and the add-on registry; nothing here is a
 * hardcoded price or allowance. Browser-safe.
 */

export type BillingInterval = AddOnBillingInterval;

/**
 * The 3-way billing toggle. Savings are fixed copy, not computed: the ladder
 * rounds each effective monthly price down to a whole dollar, so the exact
 * discount lands at 10.0–10.3% or 20.0–20.7% and the badge says 10% / 20%.
 */
export const BILLING_INTERVALS: ReadonlyArray<{
	id: BillingInterval;
	savingsPercent?: number;
}> = [
	{ id: "month" },
	{ id: "quarter", savingsPercent: 10 },
	{ id: "year", savingsPercent: 20 },
];

export const V2_PLAN_KEYS = [
	"brandV2",
	"agencyCoreV2",
	"agencyPro",
] as const satisfies readonly PlanKey[];

export type V2PlanKey = (typeof V2_PLAN_KEYS)[number];

/** Highlighted as "Most popular". */
export const RECOMMENDED_V2_PLAN: V2PlanKey = "agencyCoreV2";

export function isV2PlanKey(key: string): key is V2PlanKey {
	return (V2_PLAN_KEYS as readonly string[]).includes(key);
}

type PlanConfig = Config["payments"]["plans"][string];
type PlanPrice = NonNullable<PlanConfig["prices"]>[number];

const plans = config.payments.plans as Config["payments"]["plans"];

/** What one plan or add-on costs on one billing interval. */
export interface IntervalPrice {
	/** Monthly-equivalent price, shown big on the card. */
	perMonth: number;
	/** What the customer is charged each billing period. */
	billed: number;
	productId: string | undefined;
	currency: string;
}

function matchesInterval(
	price: { interval?: string; intervalCount?: number },
	interval: BillingInterval,
): boolean {
	const count = price.intervalCount ?? 1;
	if (interval === "year") return price.interval === "year" && count === 1;
	if (interval === "quarter")
		return price.interval === "month" && count === 3;
	return price.interval === "month" && count === 1;
}

/** Months covered by one billing period of a recurring price. */
export function monthsPerBillingPeriod(price: {
	interval?: string;
	intervalCount?: number;
}): number {
	const count = price.intervalCount ?? 1;
	return price.interval === "year" ? 12 * count : count;
}

function toIntervalPrice(price: {
	amount: number;
	interval?: string;
	intervalCount?: number;
	productId: string | undefined;
	currency: string;
}): IntervalPrice {
	return {
		// The ladder's term prices are whole-dollar monthly figures times the
		// months, so this divides exactly; rounding only guards a typo.
		perMonth: Math.round(price.amount / monthsPerBillingPeriod(price)),
		billed: price.amount,
		productId: price.productId,
		currency: price.currency,
	};
}

/** A plan's price on one billing interval, from config. */
export function getPlanIntervalPrice(
	planKey: string,
	interval: BillingInterval,
): IntervalPrice | undefined {
	const price = plans[planKey]?.prices?.find(
		(p): p is Extract<PlanPrice, { type: "recurring" }> =>
			p.type === "recurring" && !p.hidden && matchesInterval(p, interval),
	);
	return price ? toIntervalPrice(price) : undefined;
}

/**
 * The exact price an existing subscriber switches to for a plan on a billing
 * interval: monthly (interval month, count 1), quarterly (month, count 3) or
 * yearly. Never falls back to another interval; undefined when the plan has
 * no price for it.
 */
export function planChangeTargetPriceId(
	planKey: string,
	interval: BillingInterval,
): string | undefined {
	return getPlanIntervalPrice(planKey, interval)?.productId || undefined;
}

export interface V2PlanCard {
	key: V2PlanKey;
	recommended: boolean;
	prices: Record<BillingInterval, IntervalPrice | undefined>;
	prompts: number;
	credits: number;
	auditPages: number;
	rewrites: number;
	scoredPages: number;
}

function intervalPrices(
	get: (interval: BillingInterval) => IntervalPrice | undefined,
): Record<BillingInterval, IntervalPrice | undefined> {
	return {
		month: get("month"),
		quarter: get("quarter"),
		year: get("year"),
	};
}

/** The v2 plan cards, in ladder order, with allowances from entitlements. */
export function getV2PlanCards(): V2PlanCard[] {
	return V2_PLAN_KEYS.map((key) => {
		const e = getEntitlements(key);
		return {
			key,
			recommended: key === RECOMMENDED_V2_PLAN,
			prices: intervalPrices((i) => getPlanIntervalPrice(key, i)),
			prompts: e.trackedPrompts,
			credits: e.monthlyCredits,
			auditPages: e.siteAuditPages,
			rewrites: e.citationRewrites,
			scoredPages: e.citationScoringPages,
		};
	});
}

export interface PerplexityTier {
	addOnKey: AddOnKey;
	/** The v2 plan whose prompt bundle this pool is sized to. */
	planKey: V2PlanKey | undefined;
	prompts: number;
	prices: Record<BillingInterval, IntervalPrice | undefined>;
}

/** Perplexity daily-tracking pools, smallest first, priced per interval. */
export function getPerplexityTiers(): PerplexityTier[] {
	return ADD_ON_KEYS.filter(
		(key) =>
			ADD_ONS[key].platform === "perplexity" &&
			ADD_ONS[key].sellable === "self_serve",
	)
		.map((key) => {
			const prompts = ADD_ONS[key].poolPrompts ?? 0;
			return {
				addOnKey: key,
				planKey: V2_PLAN_KEYS.find(
					(plan) => getEntitlements(plan).trackedPrompts === prompts,
				),
				prompts,
				prices: intervalPrices((interval) => {
					const price = getAddOnPrice(key, interval);
					return price ? toIntervalPrice(price) : undefined;
				}),
			};
		})
		.sort((a, b) => a.prompts - b.prompts);
}

/** The lowest Perplexity monthly price, for "from $29/mo" copy. */
export function perplexityFromMonthly(): number | null {
	const monthly = getPerplexityTiers()
		.map((tier) => tier.prices.month?.perMonth)
		.filter((n): n is number => typeof n === "number");
	return monthly.length ? Math.min(...monthly) : null;
}

/**
 * True for a subscription plan from the pre-October-2026 ladder once the new
 * pricing is live: the plans existing subscribers keep, labeled
 * "(legacy pricing)" in the app.
 */
export function isLegacyPricingPlan(planKey: string | null | undefined) {
	if (!planKey || !isPricingV2Live()) return false;
	const plan = plans[planKey];
	return (
		plan?.generation === "v1" &&
		!!plan.prices?.some((p) => p.type === "recurring")
	);
}

/** The v2 card on each rung of the ladder (`getPlanTier`). */
const V2_PLAN_BY_TIER: Readonly<Record<string, V2PlanKey>> = {
	brand: "brandV2",
	core: "agencyCoreV2",
	pro: "agencyPro",
};

/**
 * The v2 card a subscription plan sits on: v1 Brand → Brand, v1 Agency Core
 * (flat or usage-based) → Agency Core, and each v2 plan → itself. Null for
 * anything that isn't a configured subscription plan on one of those rungs
 * (PAYG, enterprise, lifetime, unknown keys).
 */
export function v2PlanKeyForPlan(
	planKey: string | null | undefined,
): V2PlanKey | null {
	if (!planKey) return null;
	const isSubscriptionPlan = !!plans[planKey]?.prices?.some(
		(p) => p.type === "recurring",
	);
	if (!isSubscriptionPlan) return null;
	const tier = getPlanTier(planKey);
	return (tier && V2_PLAN_BY_TIER[tier]) || null;
}

function toBillingInterval(
	value: string | null | undefined,
): BillingInterval | null {
	return value === "month" || value === "quarter" || value === "year"
		? value
		: null;
}

/** A paying subscriber's plan, as a v2 card and a billing interval. */
export interface CurrentPlanPrice {
	planKey: V2PlanKey;
	interval: BillingInterval;
}

/**
 * Where a paying subscriber sits on the v2 cards, from the organization's
 * plan info (`subscriptionPlanType` + `subscriptionInterval`). Null for
 * trials, PAYG and anyone without a paid subscription. An interval that was
 * never recorded counts as monthly, as the invoice handler assumes.
 */
export function currentPlanPrice(
	planInfo:
		| {
				planId: string | null;
				subscriptionInterval?: string | null;
				hasActiveSubscription: boolean;
		  }
		| null
		| undefined,
): CurrentPlanPrice | null {
	if (!planInfo?.hasActiveSubscription) return null;
	const planKey = v2PlanKeyForPlan(planInfo.planId);
	if (!planKey) return null;
	return {
		planKey,
		interval: toBillingInterval(planInfo.subscriptionInterval) ?? "month",
	};
}

/**
 * True when `planKey` on `interval` is the subscriber's own plan tier and
 * billing interval, which can never start a switch. Compared by tier, so a
 * legacy Agency Core subscriber's own price is Agency Core on their interval.
 * The same plan on another interval can still be switched to.
 */
export function isCurrentPlanPrice(
	current: CurrentPlanPrice | null,
	planKey: string,
	interval: BillingInterval,
): boolean {
	return (
		!!current &&
		current.interval === interval &&
		v2PlanKeyForPlan(planKey) === current.planKey
	);
}

const USD = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	maximumFractionDigits: 0,
});

export function formatUsd(amount: number): string {
	return USD.format(amount);
}

/** One schema.org Offer in the pricing-page Product JSON-LD. */
export interface PricingOffer {
	"@type": "Offer";
	name: string;
	price: string;
	priceCurrency: string;
	description: string;
	priceSpecification?: {
		"@type": "RecurringPaymentsPricing";
		billingDuration: "P1M";
	};
}

const MONTHLY: PricingOffer["priceSpecification"] = {
	"@type": "RecurringPaymentsPricing",
	billingDuration: "P1M",
};

/**
 * The v2 pricing page's Offer list: every sellable v2 plan at its monthly
 * price, pay-as-you-go credits, the AI Visibility Report and the Perplexity
 * add-on pools. Plans come from `getSellablePlanKeys()`, so a plan that isn't
 * on sale is never advertised.
 */
export function buildPricingV2Offers({
	planTitles,
	aiReportPrice,
}: {
	planTitles: Record<V2PlanKey, string>;
	/** Numeric USD price of one AI Visibility Report. */
	aiReportPrice: number;
}): PricingOffer[] {
	const sellable = getSellablePlanKeys();
	const planOffers = V2_PLAN_KEYS.filter((key) =>
		sellable.includes(key),
	).flatMap((key): PricingOffer[] => {
		const price = getPlanIntervalPrice(key, "month");
		if (!price) return [];
		const e = getEntitlements(key);
		return [
			{
				"@type": "Offer",
				name: planTitles[key],
				price: String(price.billed),
				priceCurrency: price.currency,
				priceSpecification: MONTHLY,
				description: `${e.trackedPrompts.toLocaleString("en-US")} prompts tracked daily on ChatGPT, Gemini and Google AI Overviews, ${e.monthlyCredits.toLocaleString("en-US")} credits and ${e.siteAuditPages.toLocaleString("en-US")} site audit pages a month.`,
			},
		];
	});

	const payg = plans.paygCredits;
	const paygPrice = payg?.prices?.[0];
	const paygOffer: PricingOffer = {
		"@type": "Offer",
		name: "Pay-As-You-Go Credits",
		price: String(paygPrice?.amount ?? 1),
		priceCurrency: paygPrice?.currency ?? "USD",
		description: `Credits bought as needed with no subscription, ${formatUsd(payg?.minPurchaseAmount ?? 15)} minimum purchase.`,
	};

	const reportOffer: PricingOffer = {
		"@type": "Offer",
		name: "AI Visibility Report",
		price: String(aiReportPrice),
		priceCurrency: "USD",
		description:
			"One-time AI visibility report covering ChatGPT, Gemini, Google AI Overviews, Perplexity and Claude.",
	};

	const perplexityOffers = getPerplexityTiers().flatMap(
		(tier): PricingOffer[] => {
			const price = tier.prices.month;
			if (!price) return [];
			return [
				{
					"@type": "Offer",
					name: `Perplexity daily tracking add-on, ${tier.prompts.toLocaleString("en-US")} prompts`,
					price: String(price.billed),
					priceCurrency: price.currency,
					priceSpecification: MONTHLY,
					description:
						"Runs a property's tracked prompts on Perplexity every day, alongside ChatGPT, Gemini and Google AI Overviews.",
				},
			];
		},
	);

	return [...planOffers, paygOffer, reportOffer, ...perplexityOffers];
}
